import { css } from 'aphrodite';
import * as React from 'react';
import { IPhoneNumber } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { useTelephony } from '../../contexts/telephony';
import { useTysonCoaching } from '../../voice-agent/contexts/TysonCoachingContext';
import { startCoachingBeforeCall } from '../../voice-agent/utils/startCoachingBeforeCall';
import { styleSheet } from './styles';

interface IProps {
	companyId?: string;
	contactId?: string;
	leadServedId?: string;
	isDisabled?: boolean;
	phoneNumber: IPhoneNumber;
	renderFallback?: (href: string) => JSX.Element;
	renderWithProvider?: (onClick: () => void) => JSX.Element;
	isDirectLine?: boolean;
}

const callHref = (phoneNumber: string) => {
	return `tel:${phoneNumber}`;
};

const CallButtonWrapper: React.FC<IProps> = ({
	companyId,
	contactId,
	leadServedId,
	isDisabled,
	phoneNumber,
	renderFallback,
	renderWithProvider,
}) => {
	const userSession = useUserSession();
	const { initiateCall, phoneCall, telephonyConfiguration } = useTelephony();
	const coaching = useTysonCoaching();
	const [isStartingCoaching, setIsStartingCoaching] = React.useState(false);
	const realtimeCoachingEnabled =
		userSession.user?.userPreferences?.coffeePreferences?.realtimeCoachingEnabled ?? false;

	// Use refs to track latest coaching values to avoid stale closure issues in polling
	const coachingRef = React.useRef(coaching);
	React.useEffect(() => {
		coachingRef.current = coaching;
	}, [coaching]);

	const hasProvider = telephonyConfiguration?.provider;
	const phoneNumberE164 = phoneNumber?.metadata?.e164;

	const call = async () => {
		// Start coaching before initiating the call if realtime coaching is enabled
		if (realtimeCoachingEnabled && companyId && contactId) {
			// Only initialize coaching if not already active and ready for the SAME company
			// The Queue auto-start may have used a different contactId (contacts[0]) than the one
			// whose phone number was clicked, but coaching is per-company so company match is sufficient.
			// The RealCallCoachingBridge sends "Starting a new call with {contactName}" on dial to orient the AI.
			const sameCompany = coaching.contextData?.companyId === companyId;
			const alreadyReady =
				coaching.isActive && coaching.conversationId && !coaching.isCreatingConversation && sameCompany;

			if (!alreadyReady) {
				setIsStartingCoaching(true);
				try {
					await startCoachingBeforeCall({
						companyId,
						contactId,
						leadServedId,
						startCoaching: coaching.startCoaching,
						// Use refs to get latest values during polling (avoids stale closures)
						getConversationId: () => coachingRef.current.conversationId,
						getIsAIResponding: () => coachingRef.current.isAIResponding,
						getIsCreatingConversation: () => coachingRef.current.isCreatingConversation,
					});
				} catch (error) {
					console.error('Failed to start pre-call coaching:', error);
					// Continue with call even if coaching fails
				} finally {
					setIsStartingCoaching(false);
				}
			}
		}

		initiateCall(
			phoneNumber,
			companyId,
			contactId,
			realtimeCoachingEnabled ? (coachingRef.current.conversationId ?? undefined) : undefined
		);
	};

	const onClick = !isDisabled && !phoneCall && !isStartingCoaching ? call : null;

	if (hasProvider) {
		return renderWithProvider(onClick);
	}

	return renderFallback(callHref(phoneNumberE164));
};

export const CallButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.callCta)} href={href}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Call</span>
			</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.callCta)} onClick={onClick}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Call</span>
			</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};

export const DirectLineCallButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.directLineCta)} href={href}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Direct</span>
			</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.directLineCta)} onClick={onClick}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Direct</span>
			</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};

export const SmallCallButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.smallCallButton)} href={href}>
			<span>Call</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.smallCallButton)} onClick={onClick}>
			<span>Call</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};

export const SmallDirectLineButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.smallDirectLineButton)} href={href}>
			<span>Direct</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.smallDirectLineButton)} onClick={onClick}>
			<span>Direct</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};
